import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AppButton from "../design-system/components/AppButton";
import AppCallout from "../design-system/components/AppCallout";
import AppCheckbox from "../design-system/components/AppCheckbox";
import AppDialog from "../design-system/components/AppDialog";
import AppDialogBody from "../design-system/components/AppDialogBody";
import AppDialogFooter from "../design-system/components/AppDialogFooter";
import AppDialogHeader from "../design-system/components/AppDialogHeader";
import AppField from "../design-system/components/AppField";
import AppInput from "../design-system/components/AppInput";
import AppNavigator from "../design-system/components/AppNavigator";
import AppRadioGroup from "../design-system/components/AppRadioGroup";
import AppText from "../design-system/components/AppText";
import AppTooltip from "../design-system/components/AppTooltip";
import {
  IconDrag,
  IconImport,
  IconInfo,
} from "../design-system/icons";
import "../design-system/styles/tokens.css";
import "../design-system/styles/components.css";
import "./DownloadBuilder.css";

const FORMAT_EXCEL = "excel";
const FORMAT_PDF = "pdf";
const DEFAULT_DOCUMENT_SETTINGS = {
  footer_text: "",
  orientation: "landscape",
  pdf_title: "",
  pdf_subtitle: "",
};

const normalizeIds = (ids, fallback) => (Array.isArray(ids) && ids.length ? ids : fallback);

const formatDateForDisplay = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : "";
};

const parseDisplayDate = (value) => {
  const match = String(value || "").trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return null;
  const [, day, month, year] = match;
  const candidate = new Date(`${year}-${month}-${day}T00:00:00`);
  if (
    Number.isNaN(candidate.getTime()) ||
    candidate.getFullYear() !== Number(year) ||
    candidate.getMonth() + 1 !== Number(month) ||
    candidate.getDate() !== Number(day)
  ) {
    return null;
  }
  return `${year}-${month}-${day}`;
};

const monthValueToDate = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return new Date();
  return new Date(Number(match[1]), Number(match[2]) - 1, 1);
};

const dateToMonthValue = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const rangeForMonthValue = (value) => {
  const start = monthValueToDate(value);
  const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
  return {
    start_date: dateToIsoValue(start),
    end_date: dateToIsoValue(end),
  };
};

const formatMonthLabel = (date, locale) => {
  const label = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const startOfIsoWeek = (value) => {
  const date = value instanceof Date ? new Date(value) : new Date(`${value || ""}T00:00:00`);
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
  const day = safeDate.getDay() || 7;
  safeDate.setHours(0, 0, 0, 0);
  safeDate.setDate(safeDate.getDate() - day + 1);
  return safeDate;
};

const dateToIsoValue = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const isoWeekNumber = (date) => {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil((((target - yearStart) / 86400000) + 1) / 7);
};

const formatWeekLabel = (date, locale, calendarWeekLabel) => {
  const monday = startOfIsoWeek(date);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const format = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit" });
  const endFormat = new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return `${calendarWeekLabel} ${isoWeekNumber(monday)} · ${format.format(monday)}–${endFormat.format(sunday)}`;
};

function FormattedTimeframeInput({
  value,
  placeholder,
  formatValue,
  parseValue,
  invalid = false,
  onChange,
  onValidityChange,
}) {
  const [displayValue, setDisplayValue] = useState(() => formatValue(value));

  useEffect(() => {
    setDisplayValue(formatValue(value));
  }, [formatValue, value]);

  const handleChange = (event) => {
    const nextDisplayValue = event.target.value;
    setDisplayValue(nextDisplayValue);
    if (!nextDisplayValue.trim()) {
      onValidityChange?.(false);
      onChange?.("");
      return;
    }
    const parsedValue = parseValue(nextDisplayValue);
    onValidityChange?.(!parsedValue);
    if (parsedValue) onChange?.(parsedValue);
  };

  return (
    <AppInput
      type="text"
      inputMode="numeric"
      value={displayValue}
      placeholder={placeholder}
      invalid={invalid}
      onChange={handleChange}
    />
  );
}

function BuilderSection({ title, infoTooltip, children }) {
  return (
    <section className="download-builder__section">
      <div className="download-builder__section-heading">
        <AppText as="h3" variant="heading-4">{title}</AppText>
        {infoTooltip ? (
          <AppTooltip content={infoTooltip} placement="right">
            <span
              className="download-builder__section-info"
              tabIndex={0}
              role="img"
              aria-label={infoTooltip}
            >
              <IconInfo sx={{ fontSize: 16 }} />
            </span>
          </AppTooltip>
        ) : null}
      </div>
      <div className="download-builder__section-content">{children}</div>
    </section>
  );
}

function DocumentTable({ columns, rows }) {
  return (
    <div className="download-builder__document-table-wrap">
      <table className="download-builder__document-table">
        <thead>
          <tr>
            {columns.map((column) => <th key={column.id}>{column.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`document-row-${rowIndex}`}>
              {columns.map((column) => (
                <td key={column.id}>{String(row?.[column.id] ?? "")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PdfPreview({
  sections,
  settings,
  emptyLabel,
  pageLabel,
}) {
  const isPortrait = settings.orientation === "portrait";
  const pages = sections.length
    ? sections
    : [{ id: "pdf-preview", columns: [], rows: [] }];
  return (
    <div className="download-builder__pdf-canvas">
      {pages.map((section, pageIndex) => (
        <article
          key={section.id}
          className={`download-builder__pdf-page${isPortrait ? " is-portrait" : ""}`}
        >
          {settings.pdf_title || settings.pdf_subtitle ? (
            <header className="download-builder__pdf-document-header">
              {settings.pdf_title ? (
                <AppText as="h4" variant="heading-3">{settings.pdf_title}</AppText>
              ) : null}
              {settings.pdf_subtitle ? (
                <AppText variant="body-3" tone="secondary">{settings.pdf_subtitle}</AppText>
              ) : null}
            </header>
          ) : null}
          {section.rows.length ? (
            <DocumentTable columns={section.columns} rows={section.rows} />
          ) : (
            <AppText variant="body-3" tone="tertiary">{emptyLabel}</AppText>
          )}
          <footer className="download-builder__pdf-footer">
            <span>{settings.footer_text || ""}</span>
            <span>{pageLabel} {pageIndex + 1}</span>
          </footer>
        </article>
      ))}
    </div>
  );
}

function ExcelPreview({
  sections,
  activeSection,
  activeSectionId,
  onSectionChange,
  emptyLabel,
  tabsAriaLabel,
}) {
  return (
    <div className="download-builder__excel-workbook">
      <div className="download-builder__excel-sheet">
        {activeSection?.rows?.length ? (
          <DocumentTable columns={activeSection.columns} rows={activeSection.rows} />
        ) : (
          <AppText variant="body-3" tone="tertiary">{emptyLabel}</AppText>
        )}
      </div>
      <div className="download-builder__excel-tabs" role="tablist" aria-label={tabsAriaLabel}>
        {sections.map((section) => (
          <button
            key={section.id}
            type="button"
            role="tab"
            aria-selected={section.id === activeSectionId}
            className={`download-builder__excel-tab${section.id === activeSectionId ? " is-active" : ""}`}
            onClick={() => onSectionChange(section.id)}
          >
            {section.title}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function DownloadBuilder({
  open,
  onClose,
  title,
  documentTitle,
  description = "",
  columns = [],
  defaultColumnIds = [],
  tabModes = [],
  defaultTabMode = "none",
  previewRows = [],
  previewSections = [],
  previewLoading = false,
  isDownloading = false,
  filterGroups = [],
  defaultFilters = {},
  timeframeOptions = [],
  defaultTimeframe = {},
  defaultFormat = FORMAT_EXCEL,
  defaultDocumentSettings = DEFAULT_DOCUMENT_SETTINGS,
  resetKey = 0,
  onFiltersChange,
  onTimeframeChange,
  onTabModeChange,
  onDownload,
  renderPreview,
  pdfEnabled = false,
  dateFieldComponent: DateFieldComponent,
}) {
  const { t, i18n } = useTranslation();
  const resolvedTitle = title || t("download_builder.title");
  const resolvedDocumentTitle = documentTitle || resolvedTitle;
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "de-CH";
  const fallbackColumnIds = useMemo(() => columns.map((column) => column.id), [columns]);
  const columnsById = useMemo(() => new Map(columns.map((column) => [column.id, column])), [columns]);
  const formatOptions = useMemo(
    () => [
      { value: FORMAT_EXCEL, label: "Excel" },
      ...(pdfEnabled ? [{ value: FORMAT_PDF, label: "PDF" }] : []),
    ],
    [pdfEnabled]
  );
  const selectableFilterGroups = useMemo(
    () => filterGroups.filter((group) => (group.options || []).length > 1),
    [filterGroups]
  );
  const [format, setFormat] = useState(defaultFormat);
  const [selectedColumnIds, setSelectedColumnIds] = useState([]);
  const [columnOrderIds, setColumnOrderIds] = useState([]);
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  const [selectedTabMode, setSelectedTabMode] = useState(defaultTabMode);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [filterQueries, setFilterQueries] = useState({});
  const [selectedTimeframe, setSelectedTimeframe] = useState({});
  const [invalidTimeframeFields, setInvalidTimeframeFields] = useState({});
  const [documentSettings, setDocumentSettings] = useState(DEFAULT_DOCUMENT_SETTINGS);
  const [activePreviewSectionId, setActivePreviewSectionId] = useState("");

  const selectedColumns = useMemo(
    () =>
      columnOrderIds
        .filter((id) => selectedColumnIds.includes(id))
        .map((id) => columnsById.get(id))
        .filter(Boolean),
    [columnOrderIds, columnsById, selectedColumnIds]
  );
  const orderedColumns = useMemo(
    () => columnOrderIds.map((id) => columnsById.get(id)).filter(Boolean),
    [columnOrderIds, columnsById]
  );
  const requiredColumnIds = useMemo(
    () => columns.filter((column) => column.required).map((column) => column.id),
    [columns]
  );
  const selectedTimeframeMode = selectedTimeframe.mode || timeframeOptions[0]?.id || "";
  const monthNavigatorItems = useMemo(() => {
    const selectedMonth = monthValueToDate(selectedTimeframe.month);
    return Array.from({ length: 49 }, (_, index) => {
      const offset = index - 24;
      const date = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + offset, 1);
      return {
        value: dateToMonthValue(date),
        label: formatMonthLabel(date, locale),
      };
    });
  }, [locale, selectedTimeframe.month]);
  const weekNavigatorItems = useMemo(() => {
    const selectedWeek = startOfIsoWeek(selectedTimeframe.week);
    return Array.from({ length: 49 }, (_, index) => {
      const date = new Date(selectedWeek);
      date.setDate(selectedWeek.getDate() + ((index - 24) * 7));
      return {
        value: dateToIsoValue(date),
        label: formatWeekLabel(date, locale, t("download_builder.calendar_week")),
      };
    });
  }, [locale, selectedTimeframe.week, t]);
  const isTimeframeInvalid =
    (selectedTimeframeMode === "month" &&
      !selectedTimeframe.month) ||
    (selectedTimeframeMode === "week" &&
      !selectedTimeframe.week) ||
    (selectedTimeframeMode === "range" &&
      (Boolean(invalidTimeframeFields.start_date) ||
        Boolean(invalidTimeframeFields.end_date) ||
        !selectedTimeframe.start_date ||
        !selectedTimeframe.end_date ||
        selectedTimeframe.end_date < selectedTimeframe.start_date));
  const normalizedPreviewSections = useMemo(() => {
    const sourceSections = previewSections.length
      ? previewSections
      : [{
        id: "none",
        title: t("download_builder.data"),
        columns: selectedColumns,
        rows: previewRows,
      }];
    return sourceSections
      .map((section) => {
        const sectionColumns = Array.isArray(section.columns) && section.columns.length
          ? section.columns
          : selectedColumns;
        const sectionColumnIds = sectionColumns.map((column) => column.id);
        const intersectsSelectableColumns = sectionColumnIds.some((id) => columnsById.has(id));
        return {
          ...section,
          columns: intersectsSelectableColumns
            ? sectionColumns.filter((column) => selectedColumnIds.includes(column.id))
            : sectionColumns,
          rows: Array.isArray(section.rows) ? section.rows : [],
        };
      });
  }, [
    columnsById,
    previewRows,
    previewSections,
    selectedColumnIds,
    selectedColumns,
    t,
  ]);
  const activePreviewSection =
    normalizedPreviewSections.find((section) => section.id === activePreviewSectionId)
    || normalizedPreviewSections[0]
    || null;
  const hasPreviewData = normalizedPreviewSections.some((section) => section.rows.length > 0);

  useEffect(() => {
    if (!open) return;
    setFormat(defaultFormat === FORMAT_PDF && pdfEnabled ? FORMAT_PDF : FORMAT_EXCEL);
    setSelectedColumnIds(normalizeIds(defaultColumnIds, fallbackColumnIds));
    setColumnOrderIds([...fallbackColumnIds]);
    setDraggedColumnId(null);
    setSelectedTabMode(defaultTabMode || "none");
    setSelectedFilters(defaultFilters || {});
    setFilterQueries({});
    setSelectedTimeframe(defaultTimeframe || {});
    setInvalidTimeframeFields({});
    setDocumentSettings({
      ...DEFAULT_DOCUMENT_SETTINGS,
      pdf_title: resolvedDocumentTitle,
      pdf_subtitle: description,
      ...(defaultDocumentSettings || {}),
    });
    setActivePreviewSectionId("");
    // A new open/resetKey combination starts a fresh download session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resetKey]);

  useEffect(() => {
    if (!normalizedPreviewSections.length) {
      setActivePreviewSectionId("");
      return;
    }
    if (!normalizedPreviewSections.some((section) => section.id === activePreviewSectionId)) {
      setActivePreviewSectionId(normalizedPreviewSections[0].id);
    }
  }, [activePreviewSectionId, normalizedPreviewSections]);

  const updateFilters = (nextFilters) => {
    setSelectedFilters(nextFilters);
    onFiltersChange?.(nextFilters);
  };

  const updateTimeframe = (patch) => {
    const next = { ...selectedTimeframe, ...patch };
    setSelectedTimeframe(next);
    onTimeframeChange?.(next);
  };

  const toggleId = (setter, id) => {
    if (requiredColumnIds.includes(id)) return;
    setter((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  };

  const toggleAllIds = (setter, allIds) => {
    setter((current) => {
      const allSelected = allIds.length > 0 && allIds.every((id) => current.includes(id));
      return allSelected ? [...requiredColumnIds] : [...allIds];
    });
  };

  const toggleFilterOption = (groupId, optionId, optionIds) => {
    const values = Array.isArray(selectedFilters[groupId]) ? selectedFilters[groupId] : [];
    const currentValues = values.length ? values : optionIds;
    const nextValues = currentValues.includes(optionId)
      ? currentValues.filter((id) => id !== optionId)
      : [...currentValues, optionId];
    const normalizedValues = optionIds.length > 0 && optionIds.every((id) => nextValues.includes(id))
      ? []
      : nextValues;
    updateFilters({ ...selectedFilters, [groupId]: normalizedValues });
  };

  const moveColumn = (columnId, targetColumnId) => {
    if (!columnId || columnId === targetColumnId) return;
    setColumnOrderIds((current) => {
      const index = current.indexOf(columnId);
      const targetIndex = current.indexOf(targetColumnId);
      if (index < 0 || targetIndex < 0) return current;
      const next = [...current];
      next.splice(index, 1);
      next.splice(targetIndex, 0, columnId);
      return next;
    });
  };

  const handleDownload = () => {
    if (!onDownload || (format === FORMAT_PDF && !pdfEnabled)) return;
    onDownload({
      format,
      columns: selectedColumns.map((column) => column.id),
      tab_mode: selectedTabMode,
      filters: selectedFilters,
      timeframe: { ...selectedTimeframe, mode: selectedTimeframeMode },
      settings: {
        ...documentSettings,
        footer_text: format === FORMAT_PDF ? documentSettings.footer_text : "",
      },
    });
  };

  const canDownload =
    selectedColumnIds.length > 0 &&
    !isDownloading &&
    !previewLoading &&
    hasPreviewData &&
    !isTimeframeInvalid &&
    (format !== FORMAT_PDF || pdfEnabled);
  return (
    <AppDialog
      open={open}
      onClose={isDownloading ? undefined : onClose}
      size="xl"
      className="download-builder-dialog"
    >
      <AppDialogHeader title={resolvedTitle} visual={<IconImport />} />
      <AppDialogBody inset="none" className="download-builder">
        <div className="download-builder__layout">
          <aside className="download-builder__sidebar">
            {timeframeOptions.length ? (
              <BuilderSection title={t("download_builder.timeframe")}>
                {timeframeOptions.length > 1 ? (
                  <AppRadioGroup
                    labelVariant="body-1"
                    value={selectedTimeframeMode}
                    options={timeframeOptions.map((option) => ({ value: option.id, label: option.label }))}
                    onChange={(event) => {
                      const mode = event.target.value;
                      updateTimeframe({
                        mode,
                        ...(mode === "range" &&
                        (!selectedTimeframe.start_date || !selectedTimeframe.end_date)
                          ? rangeForMonthValue(selectedTimeframe.month)
                          : {}),
                      });
                    }}
                  />
                ) : null}
                {selectedTimeframeMode === "month" ? (
                  <AppNavigator
                    items={monthNavigatorItems}
                    value={selectedTimeframe.month || ""}
                    size="sm"
                    previousAriaLabel={t("download_builder.previous_month")}
                    nextAriaLabel={t("download_builder.next_month")}
                    searchPlaceholder={t("download_builder.search_month")}
                    onChange={(event) => {
                      const month = event.target.value;
                      updateTimeframe({
                        month,
                        ...rangeForMonthValue(month),
                      });
                    }}
                    className="download-builder__month-navigator"
                  />
                ) : null}
                {selectedTimeframeMode === "week" ? (
                  <AppNavigator
                    items={weekNavigatorItems}
                    value={selectedTimeframe.week || ""}
                    size="sm"
                    previousAriaLabel={t("download_builder.previous_week")}
                    nextAriaLabel={t("download_builder.next_week")}
                    searchPlaceholder={t("download_builder.search_week")}
                    onChange={(event) => updateTimeframe({ week: event.target.value })}
                    className="download-builder__month-navigator"
                  />
                ) : null}
                {selectedTimeframeMode === "range" ? (
                  <div className="download-builder__date-range">
                    {DateFieldComponent ? (
                      <>
                        <DateFieldComponent
                          label={t("download_builder.from")}
                          value={selectedTimeframe.start_date || ""}
                          required
                          onChange={(startDate) => {
                            setInvalidTimeframeFields((current) => ({ ...current, start_date: false }));
                            updateTimeframe({
                              start_date: startDate,
                              end_date:
                                selectedTimeframe.end_date && selectedTimeframe.end_date >= startDate
                                  ? selectedTimeframe.end_date
                                  : startDate,
                            });
                          }}
                        />
                        <DateFieldComponent
                          label={t("download_builder.until")}
                          min={selectedTimeframe.start_date || undefined}
                          value={selectedTimeframe.end_date || selectedTimeframe.start_date || ""}
                          required
                          onChange={(endDate) => {
                            setInvalidTimeframeFields((current) => ({ ...current, end_date: false }));
                            updateTimeframe({ end_date: endDate });
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <AppField label={t("download_builder.from")}>
                          <FormattedTimeframeInput
                            value={selectedTimeframe.start_date || ""}
                            placeholder="DD.MM.YYYY"
                            formatValue={formatDateForDisplay}
                            parseValue={parseDisplayDate}
                            invalid={Boolean(invalidTimeframeFields.start_date)}
                            onChange={(value) => updateTimeframe({ start_date: value })}
                            onValidityChange={(invalid) =>
                              setInvalidTimeframeFields((current) => ({ ...current, start_date: invalid }))
                            }
                          />
                        </AppField>
                        <AppField
                          label={t("download_builder.until")}
                          error={isTimeframeInvalid ? t("download_builder.invalid_timeframe") : undefined}
                        >
                          <FormattedTimeframeInput
                            value={selectedTimeframe.end_date || ""}
                            placeholder="DD.MM.YYYY"
                            formatValue={formatDateForDisplay}
                            parseValue={parseDisplayDate}
                            invalid={isTimeframeInvalid}
                            onChange={(value) => updateTimeframe({ end_date: value })}
                            onValidityChange={(invalid) =>
                              setInvalidTimeframeFields((current) => ({ ...current, end_date: invalid }))
                            }
                          />
                        </AppField>
                      </>
                    )}
                  </div>
                ) : null}
              </BuilderSection>
            ) : null}

            {formatOptions.length > 1 ? (
              <BuilderSection title={t("download_builder.format")}>
                <AppRadioGroup
                  direction="row"
                  labelVariant="body-1"
                  value={format}
                  options={formatOptions}
                  onChange={(event) => setFormat(event.target.value)}
                />
              </BuilderSection>
            ) : null}

            {columns.length > 1 ? (
              <BuilderSection
                title={t("download_builder.columns")}
                infoTooltip={t("download_builder.columns_info")}
              >
                <div className="download-builder__select-all">
                  <span className="download-builder__drag-spacer" aria-hidden="true" />
                  <AppCheckbox
                    label={t("download_builder.select_all")}
                    labelVariant="label-2"
                    checked={columns.length > 0 && columns.every((column) => selectedColumnIds.includes(column.id))}
                    indeterminate={
                      selectedColumnIds.length > 0 &&
                      !columns.every((column) => selectedColumnIds.includes(column.id))
                    }
                    onChange={() => toggleAllIds(setSelectedColumnIds, fallbackColumnIds)}
                  />
                </div>
                <div className="download-builder__column-list download-builder__option-list">
                  {orderedColumns.map((column) => (
                    <div
                      key={column.id}
                      className={`download-builder__column-option${
                        draggedColumnId === column.id ? " is-dragging" : ""
                      }`}
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        moveColumn(draggedColumnId || event.dataTransfer.getData("text/plain"), column.id);
                        setDraggedColumnId(null);
                      }}
                    >
                      <span
                        className="download-builder__drag-handle"
                        aria-label={t("download_builder.move_column", { column: column.label })}
                        title={t("download_builder.drag_to_reorder")}
                        draggable
                        onDragStart={(event) => {
                          setDraggedColumnId(column.id);
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", column.id);
                        }}
                        onDragEnd={() => setDraggedColumnId(null)}
                      >
                        <IconDrag sx={{ fontSize: 18 }} />
                      </span>
                      <AppCheckbox
                        label={column.label}
                        labelVariant="body-1"
                        checked={selectedColumnIds.includes(column.id)}
                        disabled={Boolean(column.required)}
                        onChange={() => toggleId(setSelectedColumnIds, column.id)}
                      />
                    </div>
                  ))}
                </div>
              </BuilderSection>
            ) : null}

            {selectableFilterGroups.length ? (
              <BuilderSection title={t("download_builder.filter")}>
                <div className="download-builder__filters">
                  {selectableFilterGroups.map((group) => {
                    const values = Array.isArray(selectedFilters[group.id]) ? selectedFilters[group.id] : [];
                    const optionIds = (group.options || []).map((option) => option.id);
                    const query = String(filterQueries[group.id] || "").trim().toLocaleLowerCase(locale);
                    const visibleOptions = query
                      ? (group.options || []).filter((option) =>
                        String(option.label || "").toLocaleLowerCase(locale).includes(query))
                      : (group.options || []);
                    const allSelected =
                      values.length === 0 ||
                      (optionIds.length > 0 && optionIds.every((id) => values.includes(id)));
                    return (
                      <div key={group.id} className="download-builder__filter-group">
                        {group.label && group.label !== t("download_builder.entries") ? (
                          <AppText variant="label-2" tone="secondary">{group.label}</AppText>
                        ) : null}
                        {group.searchable ? (
                          <AppInput
                            value={filterQueries[group.id] || ""}
                            placeholder={t("download_builder.search_group", {
                              group: group.label || t("download_builder.entries"),
                            })}
                            aria-label={t("download_builder.search_group", {
                              group: group.label || t("download_builder.entries"),
                            })}
                            onChange={(event) =>
                              setFilterQueries((current) => ({
                                ...current,
                                [group.id]: event.target.value,
                              }))
                            }
                          />
                        ) : null}
                        <div className="download-builder__select-all">
                          <AppCheckbox
                            label={t("download_builder.select_all")}
                            labelVariant="label-2"
                            checked={allSelected}
                            indeterminate={values.length > 0 && !allSelected}
                            onChange={() => updateFilters({ ...selectedFilters, [group.id]: [] })}
                          />
                        </div>
                        <div className="download-builder__option-list">
                          {visibleOptions.map((option) => (
                            <AppCheckbox
                              key={option.id}
                              label={option.label}
                              labelVariant="body-1"
                              checked={allSelected || values.includes(option.id)}
                              onChange={() => toggleFilterOption(group.id, option.id, optionIds)}
                            />
                          ))}
                          {visibleOptions.length === 0 ? (
                            <AppText variant="body-3" tone="tertiary">
                              {t("download_builder.no_data")}
                            </AppText>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </BuilderSection>
            ) : null}

            {tabModes.length > 1 ? (
              <BuilderSection title={t("download_builder.tabs")}>
                <AppRadioGroup
                  labelVariant="body-1"
                  value={selectedTabMode}
                  options={tabModes.map((mode) => ({
                    value: mode.id,
                    label: mode.label,
                  }))}
                  onChange={(event) => {
                    const nextMode = event.target.value;
                    setSelectedTabMode(nextMode);
                    setActivePreviewSectionId("");
                    onTabModeChange?.(nextMode);
                  }}
                />
              </BuilderSection>
            ) : null}

            {format === FORMAT_PDF ? (
              <>
                <BuilderSection title={t("download_builder.pdf_view")}>
                  <AppRadioGroup
                    direction="row"
                    labelVariant="body-1"
                    value={documentSettings.orientation}
                    options={[
                      { value: "landscape", label: t("download_builder.landscape") },
                      { value: "portrait", label: t("download_builder.portrait") },
                    ]}
                    onChange={(event) =>
                      setDocumentSettings((current) => ({
                        ...current,
                        orientation: event.target.value,
                      }))
                    }
                  />
                </BuilderSection>
                <BuilderSection title={t("download_builder.document_title_section")}>
                  <AppField label={t("download_builder.document_title")}>
                    <AppInput
                      value={documentSettings.pdf_title}
                      maxLength={80}
                      placeholder={t("download_builder.document_title_placeholder")}
                      onChange={(event) =>
                        setDocumentSettings((current) => ({
                          ...current,
                          pdf_title: event.target.value.slice(0, 80),
                        }))
                      }
                    />
                  </AppField>
                  <AppField label={t("download_builder.document_subtitle")}>
                    <AppInput
                      value={documentSettings.pdf_subtitle}
                      maxLength={160}
                      placeholder={t("download_builder.document_subtitle_placeholder")}
                      onChange={(event) =>
                        setDocumentSettings((current) => ({
                          ...current,
                          pdf_subtitle: event.target.value.slice(0, 160),
                        }))
                      }
                    />
                  </AppField>
                </BuilderSection>
                <BuilderSection title={t("download_builder.footer")}>
                  <AppField label={t("download_builder.footer_text")}>
                    <AppInput
                      value={documentSettings.footer_text}
                      maxLength={120}
                      placeholder={t("download_builder.footer_placeholder")}
                      onChange={(event) =>
                        setDocumentSettings((current) => ({
                          ...current,
                          footer_text: event.target.value.slice(0, 120),
                        }))
                      }
                    />
                  </AppField>
                </BuilderSection>
              </>
            ) : null}
          </aside>

          <section className="download-builder__preview">
            {!previewLoading && !hasPreviewData ? (
              <AppCallout
                variant="info"
                title={t("download_builder.no_data_title")}
                description={t("download_builder.no_data_description")}
                className="download-builder__preview-bleed"
              />
            ) : null}
            <div
              className={`download-builder__preview-pane${
                format === FORMAT_EXCEL ? " is-excel" : " is-pdf"
              }`}
            >
              {renderPreview ? (
                renderPreview({
                  format,
                  columns: selectedColumns,
                  tabMode: selectedTabMode,
                  filters: selectedFilters,
                  timeframe: { ...selectedTimeframe, mode: selectedTimeframeMode },
                  settings: documentSettings,
                  sections: normalizedPreviewSections,
                })
              ) : format === FORMAT_PDF ? (
                <PdfPreview
                  sections={normalizedPreviewSections}
                  settings={documentSettings}
                  emptyLabel={t("download_builder.no_data")}
                  pageLabel={t("download_builder.page")}
                />
              ) : (
                <ExcelPreview
                  sections={normalizedPreviewSections}
                  activeSection={activePreviewSection}
                  activeSectionId={activePreviewSection?.id || ""}
                  onSectionChange={setActivePreviewSectionId}
                  emptyLabel={t("download_builder.no_data")}
                  tabsAriaLabel={t("download_builder.excel_tabs")}
                />
              )}
            </div>
          </section>
        </div>
      </AppDialogBody>
      <AppDialogFooter>
        <AppButton variant="neutral" size="md" onClick={onClose} disabled={isDownloading}>
          {t("button.cancel")}
        </AppButton>
        <AppButton variant="primary" size="md" onClick={handleDownload} disabled={!canDownload}>
          <IconImport />
          {isDownloading
            ? t("download_builder.creating")
            : format === FORMAT_PDF
              ? t("download_builder.download_pdf")
              : t("download_builder.download_excel")}
        </AppButton>
      </AppDialogFooter>
    </AppDialog>
  );
}
